import { inject } from '@angular/core';
import { CanMatchFn } from '@angular/router';
import { catchError, of } from 'rxjs';
import { CommentAutopilotAccessStateService } from '../services/comment-autopilot-access-state.service';
import { environment } from 'src/environments/environment';

/**
 * Whether Comment Autopilot exists on this build at all.
 *
 * OFF unless the flag is present AND exactly `true`: an environment that says
 * nothing about it does not get the section. That is the point of the flag —
 * the section ships in the bundle but stays unreachable until an environment
 * asks for it by name.
 *
 * ONE predicate, read by both the router guard below and the sidebar, so the
 * menu can never offer a door the router refuses to open.
 */
export function isCommentAutopilotEnabled(): boolean {
	return environment.Flags.commentAutopilotEnabled === true;
}

/**
 * `canMatch`, not `canActivate`: a refused match falls through to the app's
 * `**` route and lands on /404, which is the honest answer for an address this
 * build does not serve. `canActivate` would match the route first and then
 * block it, leaving the URL sitting on a page that renders nothing.
 */
export const commentAutopilotEnabledGuard: CanMatchFn = () =>
	isCommentAutopilotEnabled();

/**
 * Whether THIS BRAND has been switched on for Autopilot by a platform admin.
 *
 * A second `canMatch`, listed after {@link commentAutopilotEnabledGuard}: the
 * router stops at the first one that refuses, so a build without the section
 * never asks the backend. A refusal falls through to the app's `**` route and
 * answers the not-found page, exactly like the build flag — a brand that is not
 * switched on is told nothing exists here, never that it could buy it.
 *
 * Fails closed: a read that errors answers false.
 */
export const commentAutopilotAccessGuard: CanMatchFn = () =>
	inject(CommentAutopilotAccessStateService)
		.ensureLoaded()
		.pipe(catchError(() => of(false)));
